import { store } from '../store/store';
import { bookingApiSlice } from '../api/booking/bookingApiSlice';
import {
    CancelSaveMode,
    CancellationFeeChoice,
    bookingWriteApiSlice,
} from '../api/booking/bookingWriteApiSlice';
import { BulkStepError } from '../utils/bookingBulkActions';
import { mailBlockedOf } from '../utils/customerMailGuard';
import type { BulkMessage, BulkStep } from '../utils/bookingBulkActions';

/**
 * The per-booking calls of the bookings list's bulk actions (#3016). The
 * rules (who is skipped, how a run reports) are in `utils/bookingBulkActions`;
 * this file only talks to the API.
 */

const MAIL_NOT_SENT: BulkMessage = { key: 'bookings.bulk.reason.mail_not_sent' };

/**
 * What a bulk step says about the mail it asked for: nothing when it went out;
 * the payment rule's reason when it was refused (PO, 2026-10-30) — the booking
 * was saved and approved, the customer heard nothing; otherwise "not sent".
 */
const mailMessage = (response: unknown, mailSent: boolean | undefined): BulkMessage | undefined => {
    if (mailSent === true) return undefined;
    const blocked = mailBlockedOf(response);
    return blocked ? { key: `bookings.bulk.reason.mail_blocked_${blocked.reason}` } : MAIL_NOT_SENT;
};

/**
 * Approve one reservation: the edit route's approve-only mode, which sends the
 * mail "save and send email" sends. The approval stands even when the mail did
 * not go out (PO, 2026-10-24) — reported as a warning, never as a success
 * without one.
 */
export const approveBookingStep: BulkStep = async (booking) => {
    const result = await store
        .dispatch(bookingApiSlice.endpoints.approveRoomRentalBooking.initiate({ orderId: booking.bookingId }))
        .unwrap();
    return mailMessage(result, result.mailSent);
};

/**
 * Cancel one booking with the ONE fee choice staff made for the whole run
 * (PO: "Charge fees where due" or "Waive all fees"). The fee is read fresh for
 * each booking, exactly as the single cancel dialog reads it, and sent with
 * the amount it showed, so a fee that changed in between is refused
 * (`fee_changed`) rather than charged unseen. A booking whose rule charges
 * nothing gets no fee in its request, as in the single flow.
 */
export const makeCancelBookingStep = (
    feeChoice: CancellationFeeChoice['choice'],
    saveMode: CancelSaveMode,
): BulkStep => async (booking) => {
    let quote;
    try {
        quote = await store
            .dispatch(bookingWriteApiSlice.endpoints.getCancellationFee.initiate(booking.bookingId, { forceRefetch: true }))
            .unwrap();
    } catch {
        // Unlike the single dialog, which falls back to the plain cancel and
        // lets the server refuse, a bulk run cannot ask again: the booking is
        // left as it is and named in the summary.
        throw new BulkStepError({ key: 'bookings.bulk.reason.fee_unreadable' });
    }
    const fee = quote.kind === 'fee' ? { choice: feeChoice, expectedFee: quote.amount } : undefined;
    const response = await store
        .dispatch(bookingWriteApiSlice.endpoints.cancelBooking.initiate({ bookingId: booking.bookingId, saveMode, fee }))
        .unwrap();
    if (response.invoiceCorrection && !response.invoiceCorrection.ok) {
        return {
            key: 'bookings.bulk.reason.invoice_correction_failed',
            params: { number: response.invoiceCorrection.originalNumber },
        };
    }
    return saveMode === 'save_and_send_email' && response.mailSent !== true ? MAIL_NOT_SENT : undefined;
};

/**
 * After a run: the RTK entries every action touches, once, instead of after
 * each booking. The legacy list store is refetched by the page.
 */
export const invalidateAfterBulkRun = () => {
    store.dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails', 'dashboardData']));
};
